import {useEffect, useRef, useState} from "react";
import {subMonths} from "date-fns";
import ReportUtils from "../../../utils/report-utils.js";
import FleetMaintenancesService from "../../../services/reports/fleet-maintenances-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";

const initialFilter = () => ({
    dtStart: subMonths(new Date(), 1),
    dtEnd: new Date(),
    vehicles: [],
    modalities: [],
});

const filtersLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    vehicles: "Veículos",
    modalities: "Modalidades",
}

const initialTableColumns = {
    vehicle_info: {label: "Veículo", visible: true},
    date: {label: "Data/hora", visible: true},
    modality: {label: "Modalidade", visible: true},
    user: {label: "Agente", visible: true},
    description: {label: "Observações", visible: true}
};

export default function FleetMaintenances() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [filters, setFilters] = useState(initialFilter);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [tableColumns, setTableColumns] = useState(initialTableColumns);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);
    const title = "Avarias, manutenções e vistorias";

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filtersLabels),
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, null, null);

        if (!periodValidation.ok) {
            ReportUtils.alertErrorMessage(periodValidation.message);
            setIsLoading(false);
            return;
        }

        try {
            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                vehicles: filters.vehicles?.map(i => i.value) ?? [],
                modalities: filters.modalities?.map(i => i.value) ?? [],
            }

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filtersLabels, true);

            const response = await FleetMaintenancesService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response.data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar o relatório.", error);
        } finally {
            setIsLoading(false);
        }
    }

    const clearSearch = () => {
        setFilters(initialFilter());
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    }

    useGlobalKey("Enter", apply);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className="flex">
                <Template isLoading={isLoading}>
                    <Template.Header>
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            visibilityColumns={true}
                            columns={tableColumns}
                            setColumns={setTableColumns}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={reportData?.length}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </Template.Header>
                    <Template.Body className={`${collapsed ? "p-0!" : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            toggleFilters={toggleFilters}
                            clearSearch={clearSearch}
                            apply={apply}
                        >
                            <Filters filters={filters} setFilters={setFilters} />
                            <AutoDatetime setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : '!px-[20px] !py-[5px]'}`}>
                        <NotAppliedContent reportData={reportData} />
                        <NoDataContent reportData={reportData} data={reportData?.length} />
                        <Content
                            reportData={reportData}
                            data={reportData?.length}
                            entity={headerSelected}
                            title={title}
                            collapsed={collapsed}
                            setIsLoading={setIsLoading}
                            printComponentRef={printComponentRef}
                            filters={filters}
                            tableColumns={tableColumns}
                        />
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}